/**
 * Centralized language-to-locale mapping for native-quality TTS.
 * Use this to resolve configured card language into preferred BCP-47 speech locales.
 * Priority: explicit field language > deck default > fallback.
 * Locale refinement happens AFTER language resolution (no text autodetection override).
 */

/** Base language code → ordered list of preferred BCP-47 locales. First = best match. */
export const LANGUAGE_PREFERRED_LOCALES: Record<string, string[]> = {
  en: ["en-US", "en-GB"],
  "en-US": ["en-US", "en-GB"],
  "en-GB": ["en-GB", "en-US"],
  es: ["es-ES", "es-MX"],
  "es-ES": ["es-ES", "es-MX"],
  "es-MX": ["es-MX", "es-ES"],
  fr: ["fr-FR"],
  "fr-FR": ["fr-FR"],
  de: ["de-DE"],
  "de-DE": ["de-DE"],
  it: ["it-IT"],
  "it-IT": ["it-IT"],
  nl: ["nl-NL"],
  "nl-NL": ["nl-NL"],
  pl: ["pl-PL"],
  "pl-PL": ["pl-PL"],
  pt: ["pt-BR", "pt-PT"],
  "pt-BR": ["pt-BR", "pt-PT"],
  "pt-PT": ["pt-PT", "pt-BR"],
  tr: ["tr-TR"],
  "tr-TR": ["tr-TR"],
  ko: ["ko-KR"],
  "ko-KR": ["ko-KR"],
  ru: ["ru-RU"],
  "ru-RU": ["ru-RU"],
  uk: ["uk-UA"],
  "uk-UA": ["uk-UA"],
  be: ["be-BY"],
  "be-BY": ["be-BY"],
  zh: ["zh-CN", "zh-TW", "zh-HK"],
  "zh-CN": ["zh-CN", "zh-TW"],
  "zh-TW": ["zh-TW", "zh-CN"],
  "zh-HK": ["zh-HK", "zh-TW", "zh-CN"],
  ja: ["ja-JP"],
  "ja-JP": ["ja-JP"],
  ar: ["ar-SA"],
  "ar-SA": ["ar-SA"],
  hi: ["hi-IN"],
  "hi-IN": ["hi-IN"],
  sv: ["sv-SE"],
  da: ["da-DK"],
  no: ["nb-NO"],
  "nb-NO": ["nb-NO"],
};

const DEFAULT_FALLBACK = "en-US";

/**
 * Returns ordered list of preferred speech locales for the given language code.
 * Use the first available when selecting a voice or requesting TTS.
 */
export function resolvePreferredSpeechLocales(languageCode?: string): string[] {
  const raw = (languageCode ?? "").toString().trim();
  if (!raw) return [DEFAULT_FALLBACK];
  const base = raw.split("-")[0].toLowerCase();
  const list = LANGUAGE_PREFERRED_LOCALES[raw] ?? LANGUAGE_PREFERRED_LOCALES[base];
  if (list?.length) return [...list];
  if (/^[a-z]{2}(-[A-Z]{2})?$/i.test(raw)) return [raw === "en" ? "en-US" : raw];
  return [DEFAULT_FALLBACK];
}

/**
 * Returns the best single locale for the given language code.
 * Product-consistent default when generic (e.g. en → en-US).
 */
export function resolveBestLocale(languageCode?: string, fallback = DEFAULT_FALLBACK): string {
  const list = resolvePreferredSpeechLocales(languageCode);
  return list[0] ?? fallback;
}
